import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAccessToken = () => {
  if (typeof window === 'undefined') return null;
  return window.__MIU_ACCESS_TOKEN__ || localStorage.getItem('token') || null;
};
const setAccessToken = (token) => {
  if (typeof window === 'undefined') return;
  window.__MIU_ACCESS_TOKEN__ = token || null;
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
};

const api = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000,
  withCredentials: true,
});

if (typeof window !== 'undefined') {
  const activeBackend = (window.location?.origin || '').includes('matchitup.in')
    ? window.location.origin
    : BACKEND_URL;
  window.__MIU_ACTIVE_BACKEND_URL__ = activeBackend;
}

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add auth token to all requests
// If a caller already set Authorization explicitly (e.g. BrewCirclesManager passing
// an admin token), respect it — don't overwrite with the stored user token.
api.interceptors.request.use((config) => {
  if (!config.headers.Authorization) {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  config.withCredentials = true;
  return config;
});

// Handle auth errors with automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Another refresh is in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Call the refresh endpoint — use runtime URL so production matchitup.in
        // doesn't accidentally hit the old preview backend domain.
        const activeUrl = (typeof window !== 'undefined' && window.__MIU_ACTIVE_BACKEND_URL__) || BACKEND_URL;
        const response = await axios.post(`${activeUrl}/api/auth/refresh`, {}, { withCredentials: true });

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Store new access token in memory only
        setAccessToken(access_token);

        // Update the failed request with new token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Process queued requests
        processQueue(null, access_token);

        // Dispatch event to notify AuthContext
        window.dispatchEvent(new CustomEvent('auth:token-refreshed', {
          detail: { token: access_token, refreshToken: newRefreshToken || null }
        }));

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        console.error('Token refresh failed:', refreshError);
        setAccessToken(null);
        processQueue(refreshError, null);
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
export { getAccessToken, setAccessToken };
